import { InAppBrowser, DefaultSystemBrowserOptions, DefaultWebViewOptions } from '@capacitor/inappbrowser'
import { Browser } from '@capacitor/browser'
import { Share } from '@capacitor/share'
import { FileOpener } from '@capacitor-community/file-opener'
import { isNative, isWeb } from './platform.utils'

/**
 * Browser Utilities
 * Utilities for in-app browser functionality using @capacitor/inappbrowser
 */

/**
 * Map presentation style to iOS view style
 * @param {string} presentationStyle - Old presentation style ('fullscreen', 'popover')
 * @returns {string} iOS view style enum value
 */
const mapPresentationStyleToIOSViewStyle = (presentationStyle) => {
  const styleMap = {
    'fullscreen': 'FULL_SCREEN',
    'popover': 'PAGE_SHEET',
    'page_sheet': 'PAGE_SHEET',
    'form_sheet': 'FORM_SHEET'
  }
  return styleMap[presentationStyle?.toLowerCase()] || 'FULL_SCREEN'
}

let isBrowserOpen = false;

/**
 * Open URL in in-app browser
 * @param {string} url - URL to open
 * @param {Object} options - Browser options
 * @param {string} options.mode - 'system' (default) or 'webview'
 * @param {string} options.presentationStyle - 'fullscreen' or 'popover' (for system mode)
 * @returns {Promise<void>}
 */
export const openInAppBrowser = async (url, options = {}) => {
  try {
    if (!isNative()) {
      // Fallback to window.open for web
      window.open(url, '_blank')
      return
    }

    const mode = options.mode || 'system'
    const presentationStyle = options.presentationStyle || 'fullscreen'

    if (mode === 'webview') {
      // Use webview mode
      const webViewOptions = {
        ...DefaultWebViewOptions,
        showToolbar: options.showToolbar !== false,
        showURL: options.showURL !== false,
        closeButtonText: options.closeButtonText || 'Close',
        ...options.webViewOptions
      }

      await InAppBrowser.openInWebView({
        url,
        options: webViewOptions
      })
      isBrowserOpen = true;
    } else {
      // Use system browser (default)
      const iosViewStyle = mapPresentationStyleToIOSViewStyle(presentationStyle)

      const systemBrowserOptions = {
        ...DefaultSystemBrowserOptions,
        iOS: {
          ...DefaultSystemBrowserOptions.iOS,
          viewStyle: iosViewStyle,
          ...options.iOSOptions
        },
        android: {
          ...DefaultSystemBrowserOptions.android,
          ...options.androidOptions
        }
      }

      await InAppBrowser.openInSystemBrowser({
        url,
        options: systemBrowserOptions
      })
      isBrowserOpen = true;
    }
  } catch (error) {
    console.error('Failed to open in-app browser:', error)
    // Fallback to external browser
    window.open(url, '_blank')
  }
}

/**
 * Open URL in webview (explicit function)
 * @param {string} url - URL to open
 * @param {Object} options - WebView options
 * @returns {Promise<void>}
 */
export const openInWebView = async (url, options = {}) => {
  return openInAppBrowser(url, {
    mode: 'webview',
    ...options
  })
}

/**
 * Open URL in system browser (explicit function)
 * @param {string} url - URL to open
 * @param {Object} options - System browser options
 * @returns {Promise<void>}
 */
export const openInSystemBrowser = async (url, options = {}) => {
  if (isNative() && url?.startsWith('file://')) {
    return openLocalFile(url, options.filename || 'Document')
  }
  return openInAppBrowser(url, {
    mode: 'system',
    ...options
  })
}

/**
 * Share a file or URL
 * @param {Object} options - Share options
 * @param {string} options.url - URL or file path to share
 * @param {string} options.title - Title for the share dialog
 * @param {string} options.text - Text to share
 * @returns {Promise<void>}
 */
export const shareFile = async (options = {}) => {
  try {
    if (!isNative()) {
      console.warn('Share is only available on native platforms')
      return
    }

    await Share.share({
      title: options.title || 'Share',
      text: options.text || '',
      url: options.url,
      dialogTitle: options.title || 'Share'
    })
  } catch (error) {
    console.error('Failed to share:', error)
  }
}

/**
 * Get MIME type based on file extension
 * @param {string} url - File URL or path
 * @returns {string} MIME type
 */
const getMimeType = (url) => {
  if (!url) return 'application/octet-stream'
  const extension = url.split('.').pop().toLowerCase()
  const mimeMap = {
    'pdf': 'application/pdf',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xls': 'application/vnd.ms-excel',
    'csv': 'text/csv',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'txt': 'text/plain',
    'json': 'application/json'
  }
  return mimeMap[extension] || 'application/octet-stream'
}

/**
 * Open a local file using system's default app
 * @param {string} url - File URI (file://...)
 * @param {string} filename - Filename (optional)
 * @returns {Promise<void>}
 */
export const openLocalFile = async (url, filename = '') => {
  try {
    if (!isNative()) {
      window.open(url, '_blank')
      return
    }

    await FileOpener.open({
      filePath: url,
      contentType: getMimeType(url || filename)
    })
  } catch (error) {
    console.error('Failed to open local file:', error)
    // Fallback to sharing if opening fails
    await shareFile({
      url: url,
      title: filename || 'Open File'
    })
  }
}

/**
 * Open URL in external browser
 * @param {string} url - URL to open
 * @returns {Promise<void>}
 */
export const openInExternalBrowser = async (url) => {
  try {
    if (!isNative()) {
      window.open(url, '_blank')
      return
    }

    await InAppBrowser.openInExternalBrowser({ url })
  } catch (error) {
    console.error('Failed to open external browser:', error)
    window.open(url, '_blank')
  }
}

/**
 * Close in-app browser
 * @returns {Promise<void>}
 */
export const closeInAppBrowser = async () => {
  try {
    if (!isNative() || !isBrowserOpen) {
      return;
    }

    console.log('--- ATTEMPTING TO CLOSE NATIVE BROWSER ---');

    // Close InAppBrowser and Browser in parallel with timeout
    const closeWithTimeout = (promise, label) =>
      Promise.race([
        promise.catch(err => console.warn(`${label} error:`, err)),
        new Promise(resolve => setTimeout(resolve, 3000)) // 3s timeout
      ]);

    await Promise.all([
      closeWithTimeout(InAppBrowser.close(), 'InAppBrowser.close()'),
      closeWithTimeout(Browser.close(), 'Browser.close()')
    ]);

    isBrowserOpen = false;
    console.log('--- NATIVE BROWSER CLOSE COMPLETED ---');
  } catch (error) {
    console.error('Failed to close in-app browser:', error);
    isBrowserOpen = false;
  }
}

/**
 * Check if in-app browser is available
 * @returns {boolean} True if available
 */
export const isInAppBrowserAvailable = () => {
  return isNative()
}

/**
 * Add browser event listeners
 * @param {Function} onClosed - Callback when browser closes
 * @param {Function} onPageLoaded - Callback when page loads
 * @param {Function} onNavigationCompleted - Callback when page navigation completes (receives { url })
 * @returns {Promise<void>}
 */
export const addBrowserListeners = async (onClosed, onPageLoaded, onNavigationCompleted) => {
  if (!isNative()) return

  try {
    if (onClosed) {
      await InAppBrowser.addListener('browserClosed', onClosed)
    }

    if (onPageLoaded) {
      await InAppBrowser.addListener('browserPageLoaded', onPageLoaded)
    }

    if (onNavigationCompleted) {
      await InAppBrowser.addListener('browserPageNavigationCompleted', (data) => {
        onNavigationCompleted(data)
      })
    }
  } catch (error) {
    console.error('Failed to add browser listeners:', error)
  }
}

/**
 * Remove browser event listeners
 * @returns {void}
 */
export const removeBrowserListeners = () => {
  if (!isNative()) return

  try {
    InAppBrowser.removeAllListeners()
  } catch (error) {
    console.error('Failed to remove browser listeners:', error)
  }
}

/**
 * Open URL with platform-specific handling for consent flows
 * - Web: Returns URL to be opened in iframe (caller should handle modal)
 * - Mobile: Opens in webview (to enable navigation tracking)
 * @param {string} url - URL to open
 * @param {Object} options - Browser options
 * @param {Function} onNavigationCompleted - Callback for navigation events (receives { url })
 * @returns {Promise<string|null>} Returns URL for web (to open in iframe), null for mobile
 */
export const openConsentUrl = async (url, options = {}, onNavigationCompleted) => {
  try {
    if (isWeb()) {
      // For web, return the URL so caller can open it in iframe
      return url
    }

    // For mobile, open in webview to enable navigation tracking
    if (isNative()) {
      // Use webview mode to enable browserPageNavigationCompleted event
      await openInWebView(url, {
        showToolbar: true,
        showURL: true,
        closeButtonText: 'Close',
        ...options
      })

      // Add navigation listener if callback provided
      if (onNavigationCompleted) {
        await InAppBrowser.addListener('browserPageNavigationCompleted', (data) => {
          onNavigationCompleted(data)
        })
      }

      return null
    }

    // Fallback
    window.open(url, '_blank')
    return null
  } catch (error) {
    console.error('Failed to open consent URL:', error)
    // Fallback to external browser
    window.open(url, '_blank')
    return null
  }
}
